/**
 * Area color configuration for OurNet
 * Defines 12 pastel colors for user area customization
 * Colors are optimized for visual distinction and accessibility
 */

export const AREA_COLORS = [
  { name: "Sky Blue", hex: "#B3D9FF", id: "sky-blue" },
  { name: "Mint Green", hex: "#B8E6D5", id: "mint-green" },
  { name: "Lavender", hex: "#D9CCFF", id: "lavender" },
  { name: "Peach", hex: "#FFD4B8", id: "peach" },
  { name: "Rose", hex: "#FFB8D9", id: "rose" },
  { name: "Lemon", hex: "#FFF4B8", id: "lemon" },
  { name: "Coral", hex: "#FFB8B8", id: "coral" },
  { name: "Aqua", hex: "#B8F0F0", id: "aqua" },
  { name: "Periwinkle", hex: "#C5D9FF", id: "periwinkle" },
  { name: "Sage", hex: "#D4E6CC", id: "sage" },
  { name: "Mauve", hex: "#E6CCE0", id: "mauve" },
  { name: "Sand", hex: "#FFE6CC", id: "sand" }
];

/**
 * Calculate a darker border color by subtracting 20 from each RGB component
 * Used for LocalitySelector buttons (4px darker border)
 * @param {string} hexColor - Hex color string (#RRGGBB)
 * @returns {string} Darker hex color for border
 */
export function getDarkerBorderColor(hexColor) {
  if (!hexColor || !hexColor.startsWith('#')) {
    return '#000000';
  }

  // Parse RGB components
  const r = parseInt(hexColor.substring(1, 3), 16);
  const g = parseInt(hexColor.substring(3, 5), 16);
  const b = parseInt(hexColor.substring(5, 7), 16);

  // Subtract 20 from each component (makes it darker)
  const darkerR = Math.max(0, r - 20);
  const darkerG = Math.max(0, g - 20);
  const darkerB = Math.max(0, b - 20);

  // Convert back to hex and return
  const hexR = darkerR.toString(16).padStart(2, '0');
  const hexG = darkerG.toString(16).padStart(2, '0');
  const hexB = darkerB.toString(16).padStart(2, '0');

  return `#${hexR}${hexG}${hexB}`.toUpperCase();
}

/**
 * Get colors that are already in use by user's areas
 * @param {Array} userAreas - Array of user area objects
 * @returns {Set} Set of hex colors in use (uppercase)
 */
export function getUsedColors(userAreas) {
  if (!Array.isArray(userAreas)) {
    return new Set();
  }

  return new Set(
    userAreas
      .filter(area => area && area.color_hex)
      .map(area => area.color_hex.toUpperCase())
  );
}

/**
 * Check if a color is available for selection
 * @param {string} hexColor - Hex color to check
 * @param {Set} usedColors - Set of already-used hex colors
 * @returns {boolean} True if color is available
 */
export function isColorAvailable(hexColor, usedColors) {
  if (!hexColor) {
    return false;
  }
  return !usedColors.has(hexColor.toUpperCase());
}
